import { useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";
import { fileUrl } from "@/lib/api";
import Reveal from "./Reveal";

export default function BeforeAfter() {
  const { content, t } = useSite();
  const ba = content?.before_after;
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  if (!ba?.before_url || !ba?.after_url) return null;

  const move = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <section className="py-24 sm:py-28 bg-brand-ink/50 border-y border-white/10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="stitch-x w-10 text-brand-blue" />
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-brand-blue">{t(UI.ba.eyebrow)}</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[1.02]">{t(UI.ba.title)}</h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            ref={ref}
            data-testid="before-after-slider"
            className="relative aspect-[16/10] rounded-xl overflow-hidden border border-white/10 select-none cursor-ew-resize touch-none shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)]"
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              move(e.clientX);
            }}
            onPointerMove={(e) => dragging.current && move(e.clientX)}
            onPointerUp={() => (dragging.current = false)}
            onPointerLeave={() => (dragging.current = false)}
          >
            <img src={fileUrl(ba.before_url)} alt={t(UI.ba.before)} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <img
              src={fileUrl(ba.after_url)}
              alt={t(UI.ba.after)}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            />

            <span className="absolute top-4 left-4 font-mono2 text-xs font-semibold uppercase tracking-widest bg-brand-blue text-white px-3 py-1.5 rounded-full">
              {t(UI.ba.after)}
            </span>
            <span className="absolute top-4 right-4 font-mono2 text-xs font-semibold uppercase tracking-widest bg-brand-navy/80 backdrop-blur-sm border border-white/20 text-white/85 px-3 py-1.5 rounded-full">
              {t(UI.ba.before)}
            </span>

            <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
              <div className="absolute inset-y-0 -translate-x-1/2 w-[3px] bg-white shadow-[0_0_20px_rgba(0,0,0,0.6)]" />
              <div
                data-testid="ba-handle"
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-white text-brand-navy shadow-xl"
              >
                <MoveHorizontal size={22} />
              </div>
            </div>

            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono2 text-[10px] uppercase tracking-widest text-white/80 bg-brand-navy/70 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/15">
              {t(UI.ba.drag)}
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
